import { useState } from "react";
import { toast } from "sonner";
import { Edit, Trash2, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useFetchSaccoWithdrawals,
  useDeleteSaccoWithdrawal,
} from "@saccoHooks";
import { WithdrawalForm } from "./WithdrawalForm";
import {SaccoWithdrawal} from "@/@types/sacco.models.ts";

interface WithdrawalListProps {
  membershipId: string;
  currentDepositBalance: number;
  nativeCurrency?: string;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(dateStr: string) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function WithdrawalList({
  membershipId,
  currentDepositBalance,
  nativeCurrency = 'KES',
  displayCurrency = 'KES',
  convert,
  isLoading: ratesLoading = false,
  error: ratesError = null,
}: WithdrawalListProps) {
  const { data: withdrawals = [], isLoading } = useFetchSaccoWithdrawals(membershipId);
  const deleteWithdrawal = useDeleteSaccoWithdrawal(membershipId);

  const [formOpen, setFormOpen] = useState(false);
  const [editingWithdrawal, setEditingWithdrawal] = useState<SaccoWithdrawal | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sorted = [...withdrawals].sort(
    (a, b) => new Date(a.withdrawal_date).getTime() - new Date(b.withdrawal_date).getTime()
  );

  function handleEdit(withdrawal: SaccoWithdrawal) {
    setEditingWithdrawal(withdrawal);
    setFormOpen(true);
  }

  function handleAddNew() {
    setEditingWithdrawal(undefined);
    setFormOpen(true);
  }

  function handleDeleteClick(id: string) {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deletingId) return;
    try {
      await deleteWithdrawal.mutateAsync(deletingId);
      toast.success("Withdrawal deleted successfully");
    } catch {
      toast.error("Failed to delete withdrawal");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleAddNew} size="sm">
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Withdrawal
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">Loading withdrawals...</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No withdrawals recorded yet.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((withdrawal) => (
              <TableRow key={withdrawal.id}>
                <TableCell>{formatDate(withdrawal.withdrawal_date)}</TableCell>
                <TableCell className="text-right font-medium">
                  {ratesLoading ? (
                    <Skeleton className="h-4 w-20 inline-block" />
                  ) : (() => {
                    let displayValue = Number(withdrawal.amount);
                    if (!ratesError && convert && displayCurrency !== nativeCurrency) {
                      try {
                        displayValue = convert(displayValue, nativeCurrency, displayCurrency);
                      } catch {
                        // fall back to raw amount
                      }
                    }
                    return formatCurrency(displayValue, ratesError ? nativeCurrency : displayCurrency);
                  })()}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {withdrawal.note ?? "—"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(withdrawal)}
                      aria-label="Edit withdrawal"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDeleteClick(withdrawal.id)}
                      disabled={deleteWithdrawal.isPending}
                      aria-label="Delete withdrawal"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <WithdrawalForm
        membershipId={membershipId}
        nativeCurrency={nativeCurrency}
        currentDepositBalance={currentDepositBalance}
        withdrawal={editingWithdrawal}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingWithdrawal(undefined);
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Withdrawal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this withdrawal? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
